import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
    Card, CardContent, Switch, Typography, IconButton, CircularProgress, Grid, CardActions,
    Chip, Dialog, Box, DialogTitle, DialogContent, DialogActions, Button, TextField, Select,
    MenuItem, FormControl, InputLabel, Tooltip, InputAdornment, Snackbar, Alert as MuiAlert
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    ModeEditOutlineOutlined as ModeEditOutlineOutlinedIcon,
    EditCalendarOutlined as EditCalendarOutlinedIcon,
    VisibilityOutlined as VisibilityOutlinedIcon,
    Search as SearchIcon,
    MoreTimeOutlined as MoreTimeOutlinedIcon,
    CloudSyncOutlined as CloudSyncOutlinedIcon,
    AdminPanelSettingsOutlined as AdminPanelSettingsOutlinedIcon,
    CoPresentOutlined as CoPresentOutlinedIcon,
    SchoolOutlined as SchoolOutlinedIcon,
    AssignmentTurnedIn as AssignmentTurnedInIcon,
    MenuBook as MenuBookIcon,
    Calculate as CalculateIcon

} from '@mui/icons-material';
import AbcIcon from '@mui/icons-material/Abc';import { useNavigate } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { scheduleExam, regradeExam, fetchUsersForScheduling } from '../actions/auth'; // Ensure path is correct
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useTheme } from "@mui/material";
import { tokens } from "../theme"; // Assuming tokens provides colors

// Debounce helper function
function debounce(func, wait) { /* ... */
    let timeout;
    return function executedFunction(...args) {
      const later = () => { clearTimeout(timeout); func(...args); };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
}

// Helper: Consistent Date Formatting
const formatDateTime = (dateString) => { /* ... */
    if (!dateString) return "N/A";
    try {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true });
    } catch (e) { console.error("Error formatting date:", dateString, e); return "Invalid Date"; }
};

// --- Main Component ---
const ManageMyExams = React.memo(({ exam }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();

    // === State Hooks ===
    // ... (all state hooks remain the same) ...
    const [openScheduleDialog, setOpenScheduleDialog] = useState(false);
    const [openRegradeDialog, setOpenRegradeDialog] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const [startTime, setStartTime] = useState(new Date());
    const [endTime, setEndTime] = useState(new Date());
    const [autoTransition, setAutoTransition] = useState(true);
    const [scheduleType, setScheduleType] = useState('Full');
    const [selectedUserIds, setSelectedUserIds] = useState([]); // <--- Stores selected IDs
    const [isScheduling, setIsScheduling] = useState(false);
    const [isRegrading, setIsRegrading] = useState(false);
    const [isLoadingDialogUsers, setIsLoadingDialogUsers] = useState(false);
    const [dialogUsers, setDialogUsers] = useState([]); // <--- Users for the current page
    const [dialogSearchText, setDialogSearchText] = useState('');
    const [debouncedDialogSearchText, setDebouncedDialogSearchText] = useState('');
    const [dialogPaginationModel, setDialogPaginationModel] = useState({ pageSize: 25, page: 0 });
    const [dialogRowCount, setDialogRowCount] = useState(0);

    // --- Debounce Handler ---
    const debouncedDialogSearchHandler = useRef( /* ... */
        debounce((value) => {
            setDebouncedDialogSearchText(value);
            setDialogPaginationModel(prev => ({ ...prev, page: 0 }));
        }, 500)
    ).current;

    const handleDialogSearchChange = useCallback((event) => { /* ... */
        const newSearchText = event.target.value;
        setDialogSearchText(newSearchText);
        debouncedDialogSearchHandler(newSearchText);
    }, [debouncedDialogSearchHandler]);

    // --- Data Fetching ---
    const loadDialogUsers = useCallback(async () => { /* ... (same as before) ... */
        if (!openScheduleDialog || !exam?.id) return;
        setIsLoadingDialogUsers(true);
        try {
            const data = await fetchUsersForScheduling(exam.id, dialogPaginationModel.page + 1, dialogPaginationModel.pageSize, debouncedDialogSearchText);
            setDialogUsers(Array.isArray(data.results) ? data.results : []);
            setDialogRowCount(data.count || 0);
        } catch (error) {
            console.error("Error fetching users for scheduling:", error);
            setSnackbar({ open: true, message: `Failed to load users: ${error.message}`, severity: 'error' });
            setDialogUsers([]); setDialogRowCount(0);
        } finally { setIsLoadingDialogUsers(false); }
    }, [exam?.id, openScheduleDialog, dialogPaginationModel.page, dialogPaginationModel.pageSize, debouncedDialogSearchText]);

    useEffect(() => { if (openScheduleDialog) { loadDialogUsers(); } }, [openScheduleDialog, loadDialogUsers]);

    // --- Other Event Handlers ---
    const handleNavigate = useCallback((path) => navigate(path), [navigate]);
    const handleOpenScheduleDialog = useCallback(() => { /* ... (Reset states as before) ... */
        const now = new Date();
        setStartTime(now);
        // setEndTime(new Date(now.getTime() + (exam?.duration || 120) * 60 * 1000 * 2));
        setEndTime(new Date(now.getTime() + 60 * 60 * 4*24000));

        setDialogSearchText(''); setDebouncedDialogSearchText('');
        setDialogPaginationModel({ pageSize: 25, page: 0 });
        setSelectedUserIds([]); // Clear selection
        setDialogUsers([]); // Clear user list
        setDialogRowCount(0);
        setOpenScheduleDialog(true);
    }, [exam?.duration]);
    const handleCloseScheduleDialog = useCallback(() => setOpenScheduleDialog(false), []);
    const handleOpenRegradeDialog = useCallback(() => setOpenRegradeDialog(true), []);
    const handleCloseRegradeDialog = useCallback(() => setOpenRegradeDialog(false), []);
    const handleCloseSnackbar = useCallback((_event, reason) => { if (reason === 'clickaway') return; setSnackbar(prev => ({ ...prev, open: false })); }, []);
    const handleAutoTransitionChange = useCallback((event) => setAutoTransition(event.target.checked), []);

    // --- Actions ---
    const handleRegrade = useCallback(async () => { /* ... */
        if (!exam?.id) return;
        setIsRegrading(true); handleCloseRegradeDialog();
        try { await regradeExam(exam.id); setSnackbar({ open: true, message: 'Exam regrading initiated successfully.', severity: 'success' }); }
        catch (error) { console.error("Regrade error:", error); setSnackbar({ open: true, message: `Error initiating regrade: ${error.message || 'Unknown error'}`, severity: 'error' }); }
        finally { setIsRegrading(false); }
     }, [exam?.id, handleCloseRegradeDialog]);

    const handleSchedule = useCallback(async () => { /* ... */
        if (!exam?.id) return;
        if (endTime <= startTime) { setSnackbar({ open: true, message: "End time must be after start time.", severity: 'warning' }); return; }
        if (selectedUserIds.length === 0) { setSnackbar({ open: true, message: "Please select at least one student.", severity: 'warning' }); return; }
        setIsScheduling(true);
        try { await scheduleExam(exam.id, startTime.toISOString(), endTime.toISOString(), selectedUserIds, scheduleType, autoTransition); setSnackbar({ open: true, message: 'Exam scheduled successfully.', severity: 'success' }); setSelectedUserIds([]); handleCloseScheduleDialog(); }
        catch (error) { console.error("Schedule error:", error); setSnackbar({ open: true, message: `Error scheduling exam: ${error.message || 'Unknown error'}`, severity: 'error' }); }
        finally { setIsScheduling(false); }
    }, [exam?.id, startTime, endTime, selectedUserIds, scheduleType, autoTransition, handleCloseScheduleDialog]);

    // --- >>> DERIVED STATE: Sort users on the current page to show selected first <<< ---
    const sortedDialogUsers = useMemo(() => {
        // Create a copy to avoid mutating the original state
        const usersToSort = [...dialogUsers];

        // Sort the array
        usersToSort.sort((a, b) => {
            const aIsSelected = selectedUserIds.includes(a.id);
            const bIsSelected = selectedUserIds.includes(b.id);

            if (aIsSelected && !bIsSelected) {
                return -1; // a comes first
            }
            if (!aIsSelected && bIsSelected) {
                return 1; // b comes first
            }
            // If both are selected or both are not selected,
            // maintain their original relative order (received from server)
            return 0;
        });

        return usersToSort;
    }, [dialogUsers, selectedUserIds]); // Re-sort when users or selection changes

    // --- DataGrid Columns Definition ---
    const userColumns = useMemo(() => [
        // --- Columns definition remains the same as the previous version ---
        // It will now operate on the 'sortedDialogUsers' array passed to the grid
         {
            field: 'fullName',
            headerName: 'Name & Status', // Updated header
            flex: 1.5, // Adjusted flex
            minWidth: 220, // Increased minWidth slightly
            sortable: false,
            valueGetter: (params) => {
                if (!params || !params.row) { return ''; }
                return `${params.row.first_name || ''} ${params.row.last_name || ''}`;
            },
            renderCell: ({ row }) => {
                const handleOpenExamDetails = (event, studentExamId) => { /* ... */
                     event.stopPropagation();
                    if (studentExamId) {
                        const url = `/student-exam-details/${studentExamId}`; // ADJUST URL IF NEEDED
                        window.open(url, '_blank', 'noopener,noreferrer');
                    }
                 };

                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', overflow: 'hidden' }}>
                        <>
                            {`${row?.first_name || ''} ${row?.last_name || ''}`}
                        </>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0,ml:0.5 }}>
                            {(row?.time_multiplier && parseFloat(row.time_multiplier) !== 1.0) && (
                                <Tooltip title={`Time Accommodation: ${row.time_multiplier}x`} placement="top">
                                    <IconButton size="small" sx={{ p: 0.2 }} aria-label="time accommodation info"> <MoreTimeOutlinedIcon color="info" sx={{ fontSize: '1rem' }} /> </IconButton>
                                </Tooltip>
                            )}
                            {row?.full_completion_id && (
                                <Tooltip title={`Full Completed: ${formatDateTime(row.full_completion_time)} (Click)`} placement="top">
                                    <IconButton onClick={(e) => handleOpenExamDetails(e, row.full_completion_id)} size="small" aria-label="view full completion" sx={{ p: 0.2 }}> <AssignmentTurnedInIcon sx={{ fontSize: '1rem', color: colors.greenAccent[500] }} /> </IconButton>
                                </Tooltip>
                            )}
                            {row?.english_completion_id && (
                                <Tooltip title={`English Completed: ${formatDateTime(row.english_completion_time)} (Click)`} placement="top">
                                    <IconButton onClick={(e) => handleOpenExamDetails(e, row.english_completion_id)} size="small" aria-label="view english completion" sx={{ p: 0.2 }}> <AbcIcon sx={{ fontSize: '1rem', color: colors.blueAccent[500] }} /> </IconButton>
                                </Tooltip>
                            )}
                            {row?.math_completion_id && (
                                <Tooltip title={`Math Completed: ${formatDateTime(row.math_completion_time)} (Click)`} placement="top">
                                    <IconButton onClick={(e) => handleOpenExamDetails(e, row.math_completion_id)} size="small" aria-label="view math completion" sx={{ p: 0.2 }}> <CalculateIcon sx={{ fontSize: '1rem', color: colors.redAccent[500] }} /> </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Box>
                );
            }
        },
        { field: 'email', headerName: 'Email', flex: 1.5, minWidth: 180, sortable: false },
        {
            field: 'group_names',
            headerName: 'Role',
            width: 130,
            align: 'center',
            headerAlign: 'center',
            sortable: false,
            renderCell: ({ row }) => { /* ... Role chip logic as before ... */
                 const role = row?.group_names?.[0]?.toUpperCase() || 'N/A';
                let IconComponent;
                let bgColor = colors.grey[700];
                switch (role) {
                    case 'ADMIN': IconComponent = AdminPanelSettingsOutlinedIcon; break;
                    case 'TEACHER': IconComponent = CoPresentOutlinedIcon; break;
                    case 'STUDENT': IconComponent = SchoolOutlinedIcon; bgColor = colors.greenAccent[600]; break;
                    default: IconComponent = null;
                }
                const textColor = theme.palette.getContrastText(bgColor);
                return (<Chip icon={IconComponent ? <IconComponent sx={{ color: textColor, fontSize: '1rem' }} /> : undefined} label={role} size="small" sx={{ backgroundColor: bgColor, color: textColor, width: '90%', maxWidth: '110px' }}/>);
            },
        },
    ], [colors, theme.palette]); // Dependencies

    // --- Render ---
    if (!exam) { return <Grid item xs={12} sm={6} md={4}><CircularProgress /></Grid>; }

    return (
        <Grid item xs={12} sm={6} md={4} sx={{ display: 'flex' }}>
            {/* Card component */}
            <Card sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '100%', height: '100%', boxShadow: theme.shadows[2], '&:hover': { boxShadow: theme.shadows[4] } }}>
               {/* ... CardContent and CardActions as before ... */}
                <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h5" component="div" gutterBottom noWrap sx={{ fontWeight: 'medium' }}>{exam.name}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Category: {exam.exam_type}</Typography>
                    <Chip label={exam.is_published ? 'Published' : 'Draft'} color={exam.is_published ? 'success' : 'default'} size="small" sx={{ mb: 1 }} variant={exam.is_published ? 'filled' : 'outlined'}/>
                    <Typography variant="body2" color="text.secondary">Modules: {exam.modules_count} | Duration: {exam.duration} mins</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>Created: {formatDateTime(exam.created_at)}</Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', p: theme.spacing(1, 2) }}>
                    <Box>
                        <Tooltip title="Edit Exam Details"><IconButton onClick={() => handleNavigate(`/viewExamDetails/${exam.id}`)} aria-label="edit exam details" color="primary" size="small"><ModeEditOutlineOutlinedIcon fontSize="small"/></IconButton></Tooltip>
                        <Tooltip title="Schedule Exam"><span><IconButton onClick={handleOpenScheduleDialog} aria-label="schedule exam" color="primary" disabled={!exam.is_published} size="small"><EditCalendarOutlinedIcon fontSize="small"/></IconButton></span></Tooltip>
                        <Tooltip title="Preview Exam"><IconButton onClick={() => handleNavigate(`/previewExam/${exam.id}`)} aria-label="preview exam" color="primary" size="small"><VisibilityOutlinedIcon fontSize="small"/></IconButton></Tooltip>
                    </Box>
                    <Tooltip title={exam.is_published ? "Regrade All Submissions" : "Publish exam to enable regrade"}><span><Button onClick={handleOpenRegradeDialog} size="small" variant="outlined" startIcon={isRegrading ? <CircularProgress size={16} /> : <CloudSyncOutlinedIcon fontSize="small"/>} disabled={!exam.is_published || isRegrading}>{isRegrading ? 'Regrading' : 'Regrade'}</Button></span></Tooltip>
                </CardActions>
            </Card>

            {/* Regrade Confirmation Dialog */}
            <Dialog open={openRegradeDialog} onClose={handleCloseRegradeDialog} aria-labelledby="regrade-dialog-title">
                 {/* ... content as before ... */}
                  <DialogTitle id="regrade-dialog-title">Confirm Regrade</DialogTitle>
                <DialogContent><Typography>Are you sure you want to regrade this exam? This action will recalculate scores for **all completed attempts** based on the current answer key. This cannot be undone easily.</Typography></DialogContent>
                <DialogActions sx={{ p: theme.spacing(2) }}>
                    <Button onClick={handleCloseRegradeDialog}>Cancel</Button>
                    <Button onClick={handleRegrade} variant="contained" color="warning" startIcon={isRegrading ? <CircularProgress size={20} color="inherit"/> : <CloudSyncOutlinedIcon />} disabled={isRegrading}>
                        {isRegrading ? 'Regrading...' : 'Confirm Regrade'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Schedule Exam Dialog */}
            <Dialog open={openScheduleDialog} onClose={handleCloseScheduleDialog} fullWidth maxWidth="md" aria-labelledby="schedule-dialog-title">
                <DialogTitle id="schedule-dialog-title">Schedule Exam: {exam.name}</DialogTitle>
                <DialogContent>
                    {/* Search Box */}
                    <TextField fullWidth label="Search Users" variant="outlined" value={dialogSearchText} onChange={handleDialogSearchChange} placeholder="Filter by name or email..." InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon /></InputAdornment>), }} sx={{ mb: 2 }}/>

                    {/* --- >> ALTERNATIVE: Display Selected Users Separately << --- */}
                    {/* {selectedUserIds.length > 0 && (
                        <Box mb={2} p={1} border={1} borderColor="divider" borderRadius={1}>
                            <Typography variant="subtitle2" gutterBottom>
                                Selected ({selectedUserIds.length}):
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selectedUserIds.map(id => {
                                    // Find user details - requires having users accessible or fetching details
                                    const user = dialogUsers.find(u => u.id === id) || fetchedUserDetails[id]; // Example
                                    return user ? (
                                        <Chip key={id} label={`${user.first_name} ${user.last_name}`} size="small" onDelete={() => handleDeselectUser(id)} />
                                    ) : null;
                                })}
                            </Box>
                        </Box>
                    )} */}
                    {/* --- END ALTERNATIVE --- */}


                    {/* User DataGrid */}
                    <Box sx={{ height: 450, width: '100%', mb: 3 }}>
                        <DataGrid
                            // --- >> Use the locally sorted array for display << ---
                            rows={sortedDialogUsers}
                            columns={userColumns}
                            // --- Pagination and other props remain the same ---
                            pagination paginationMode="server"
                            rowCount={dialogRowCount}
                            pageSize={dialogPaginationModel.pageSize}
                            page={dialogPaginationModel.page}
                            onPageChange={(newPage) => setDialogPaginationModel(prev => ({ ...prev, page: newPage }))}
                            onPageSizeChange={(newPageSize) => setDialogPaginationModel({ page: 0, pageSize: newPageSize })}
                            pageSizeOptions={[10, 25, 50, 100]}
                            filterMode="server"
                            checkboxSelection
                            rowSelectionModel={selectedUserIds}
                            onRowSelectionModelChange={(newSelectionModel) => { setSelectedUserIds(newSelectionModel); }}
                            keepNonExistentRowsSelected
                            loading={isLoadingDialogUsers}
                            density="compact"
                            sx={{ '--DataGrid-overlayHeight': '200px' }}
                            getRowId={(row) => row.id}
                        />
                    </Box>
                    {/* Date/Time Pickers */}
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                         {/* ... Grid for pickers as before ... */}
                         <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={6}><DateTimePicker label="Exam Window Start" value={startTime} onChange={setStartTime} minDateTime={new Date()} slotProps={{ textField: { fullWidth: true, helperText: "When students can begin." } }} /></Grid>
                            <Grid item xs={12} sm={6}><DateTimePicker label="Exam Window End" value={endTime} onChange={setEndTime} minDateTime={startTime} slotProps={{ textField: { fullWidth: true, helperText: "When access closes." } }} /></Grid>
                        </Grid>
                    </LocalizationProvider>
                    {/* Other Options */}
                    <Grid container spacing={2} alignItems="center">
                         {/* ... Grid for options as before ... */}
                         <Grid item xs={12} sm={6}><FormControl fullWidth><InputLabel id="schedule-type-label">Schedule Type</InputLabel><Select labelId="schedule-type-label" label="Schedule Type" value={scheduleType} onChange={(e) => setScheduleType(e.target.value)}><MenuItem value="Full">Full Exam</MenuItem><MenuItem value="English">English Only</MenuItem><MenuItem value="Math">Math Only</MenuItem></Select></FormControl></Grid>
                      <Grid item xs={12} sm={6}><Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'center'}, pl: { xs: 1, sm: 0 }}}><Typography variant="body2" sx={{ mr: 1 }}>Auto-Advance Modules:</Typography><Switch checked={autoTransition} onChange={handleAutoTransitionChange} /><Tooltip title="Automatically move students to the next module/break when their time for the current module expires."><IconButton size="small" sx={{ ml: 0.5 }}><InfoOutlinedIcon fontSize="small"/></IconButton></Tooltip></Box></Grid>
                    </Grid>
                </DialogContent>
                {/* DialogActions */}
                <DialogActions sx={{ p: theme.spacing(2) }}>
                     {/* ... Actions as before ... */}
                     <Button onClick={handleCloseScheduleDialog}>Cancel</Button>
                    <Button onClick={handleSchedule} variant="contained" color="primary" disabled={isScheduling || selectedUserIds.length === 0} startIcon={isScheduling ? <CircularProgress size={20} color="inherit"/> : null}>{isScheduling ? 'Scheduling...' : `Schedule (${selectedUserIds.length})`}</Button>
                </DialogActions>
            </Dialog>

            {/* Feedback Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <MuiAlert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled" elevation={6}>{snackbar.message}</MuiAlert>
            </Snackbar>
        </Grid>
    );
});

export default ManageMyExams;